/**
 * Minimal MapLibre view for the Events discovery page.
 *
 * Deliberately standalone (not a `MapView`-of-events wrapper around the
 * places map) because the Events feature's needs are different:
 *   • Pins coloured by `category`, not by Google type.
 *   • Click a pin → emit a selection via `onSelect` so the parent can
 *     open a side-drawer / bottom-sheet with the full event detail.
 *   • No origin dot, no distance labels — this is a world-view, not a
 *     "near-me" surface.
 *
 * Lazy MapLibre init matches the places page pattern.
 */
import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

import type { PlaceEvent } from '@/types/api';
import { categoryMeta } from '@/pages/events/categories';

const OPENFREEMAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

export interface EventsMapProps {
  events: PlaceEvent[];
  onSelect?: (event: PlaceEvent) => void;
}

export function EventsMap({ events, onSelect }: EventsMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markersRef = useRef<maplibregl.Marker[]>([]);

  // Compute a reasonable centre + bounds. If events have coords, fit to
  // their bounds; otherwise fall back to a world-ish centre.
  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) return; // already initialised

    const coordEvents = events.filter(e => e.lat != null && e.lng != null);
    const center: [number, number] = coordEvents.length
      ? [Number(coordEvents[0].lng), Number(coordEvents[0].lat)]
      : [0, 20];

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: OPENFREEMAP_STYLE_URL,
      center,
      zoom: coordEvents.length ? 4 : 1.5,
      attributionControl: { compact: true },
    });
    mapRef.current = map;

    return () => {
      markersRef.current.forEach(m => m.remove());
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Repaint pins whenever the events array changes.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Clear existing markers.
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const coords: Array<[number, number]> = [];
    for (const ev of events) {
      if (ev.lat == null || ev.lng == null) continue;
      const meta = categoryMeta(ev.category);
      const color = meta?.color ?? '#1488CF';

      const el = document.createElement('button');
      el.type = 'button';
      el.title = ev.title;
      el.setAttribute('aria-label', ev.title);
      el.style.cssText = `
        width: 28px; height: 28px; border-radius: 50%;
        background: ${color}; border: 2px solid white;
        box-shadow: 0 2px 6px rgba(0,0,0,0.3); cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        font-size: 14px; line-height: 1;
      `;
      el.textContent = meta?.emoji ?? '📍';
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (onSelect) onSelect(ev);
      });

      const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([Number(ev.lng), Number(ev.lat)])
        .addTo(map);
      markersRef.current.push(marker);
      coords.push([Number(ev.lng), Number(ev.lat)]);
    }

    // Fit bounds when we have >1 coord.
    if (coords.length > 1) {
      try {
        const bounds = coords.reduce(
          (b, c) => b.extend(c as [number, number]),
          new maplibregl.LngLatBounds(coords[0], coords[0]),
        );
        map.fitBounds(bounds, { padding: 40, maxZoom: 10, duration: 400 });
      } catch { /* non-fatal */ }
    } else if (coords.length === 1) {
      map.flyTo({ center: coords[0], zoom: 10, duration: 400 });
    }
  }, [events, onSelect]);

  return (
    <div
      ref={containerRef}
      className="h-[600px] w-full overflow-hidden rounded-xl border border-border"
      aria-label="Events map"
    />
  );
}
